import type { ReactNode } from 'react';
import { Briefcase, Building2, Clock, Fingerprint, Loader2, Mail, MapPin, Phone, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useAssertivaCpf } from '@/hooks/useAssertivaCpf';
import { formatarCPF } from '@/utils/formatters';

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {});
const lista = (v: unknown): Obj[] => (Array.isArray(v) ? (v as Obj[]) : []);
const txt = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(v));

const ROTULOS: Record<string, string> = {
  nome: 'Nome', cpf: 'CPF', dataNascimento: 'Nascimento', idade: 'Idade', sexo: 'Sexo', signo: 'Signo',
  situacaoCadastral: 'Situação na Receita', dataSituacaoCadastral: 'Situação desde', obitoProvavel: 'Óbito provável',
  ppe: 'Pessoa politicamente exposta', rejeitar: 'Rejeitar', rg: 'RG', ufRg: 'UF do RG',
  maeNome: 'Nome da mãe', maeCpf: 'CPF da mãe', maeDataNascimento: 'Nascimento da mãe',
};

const BLOCOS_TRATADOS = new Set([
  'dadosCadastrais', 'telefones', 'telefonesAdicionados', 'feedbackTelefones', 'emails', 'emailsAdicionados',
  'enderecos', 'enderecosAdicionados', 'participacoesEmpresas', 'possivelHistoricoProfissional',
]);

function valorLegivel(v: unknown): ReactNode {
  if (v === true) return 'Sim';
  if (v === false) return 'Não';
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'object') return <span className="font-mono text-[10px] font-normal">{JSON.stringify(v)}</span>;
  return String(v);
}

function Par({ label, valor }: { label: string; valor?: ReactNode }) {
  return (
    <div className="flex min-h-[30px] items-center justify-between gap-3 border-b border-[#F0E9E1] py-1 last:border-b-0 dark:border-white/[0.05]">
      <span className="flex-shrink-0 text-[11px] text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-right text-xs font-semibold tabular-nums text-[#2E2019] dark:text-[#E8DEDE]">
        {valor ?? <span className="font-normal text-muted-foreground/40">—</span>}
      </span>
    </div>
  );
}

function Secao({ icone, titulo, qtd, children }: { icone: ReactNode; titulo: string; qtd?: number; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex min-h-[34px] items-center gap-2 border-b bg-[#FBFAF9] px-3.5 py-1.5 dark:bg-white/[0.02]">
        <h3 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-card-foreground">
          {icone} {titulo}
        </h3>
        {qtd !== undefined && (
          <span className="inline-flex items-center rounded-full border border-border bg-[#F2EEE8] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-muted-foreground dark:bg-white/[0.06]">{qtd}</span>
        )}
      </div>
      <div className="px-3.5 py-2">{children}</div>
    </section>
  );
}

function Selo({ bom, children }: { bom: boolean; children: ReactNode }) {
  return (
    <span className={bom
      ? 'inline-flex items-center gap-1 rounded-full bg-[#EAF3EC] px-2 py-0.5 text-[10px] font-bold text-[#075E54] dark:bg-[#075E54]/20 dark:text-[#25D366]'
      : 'inline-flex items-center gap-1 rounded-full bg-[#FBEAEA] px-2 py-0.5 text-[10px] font-bold text-[#9B2C2C] dark:bg-[#9B2C2C]/20 dark:text-[#E8A0A0]'}>
      {bom ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}{children}
    </span>
  );
}

export function LeadDadosCpfAssertiva({ cpf }: { cpf: string }) {
  const { data, isLoading } = useAssertivaCpf(cpf);

  if (isLoading) {
    return <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-3 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando dados do CPF…</div>;
  }

  const resposta = obj(data?.consulta?.resultado?.resposta);
  if (!data?.consulta) {
    const status = data?.fila?.status;
    return (
      <Secao icone={<Fingerprint className="h-3 w-3 text-muted-foreground" />} titulo="Dados do CPF · Assertiva">
        <p className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {status === 'SEM_DADOS' ? 'A Assertiva não tem cadastro para este CPF.'
            : status === 'ERRO' ? `A consulta falhou: ${data?.fila?.erro_mensagem || 'tente mais tarde'}.`
            : 'Na fila de validação da Assertiva. Os dados aparecem aqui em instantes.'}
        </p>
      </Secao>
    );
  }

  const cad = obj(resposta.dadosCadastrais);
  const telefones = obj(resposta.telefones);
  const telAdd = obj(resposta.telefonesAdicionados);
  const fones: Obj[] = [
    ...lista(telefones.moveis).map((t) => ({ ...t, tipo: 'Móvel' })),
    ...lista(telAdd.moveis).map((t) => ({ ...t, tipo: 'Móvel (adicionado)' })),
    ...lista(telefones.fixos).map((t) => ({ ...t, tipo: 'Fixo' })),
    ...lista(telAdd.fixos).map((t) => ({ ...t, tipo: 'Fixo (adicionado)' })),
  ];
  const emails = [...lista(resposta.emails), ...lista(resposta.emailsAdicionados)];
  const enderecos = [...lista(resposta.enderecos), ...lista(resposta.enderecosAdicionados)];
  const empresas = lista(resposta.participacoesEmpresas);
  const profissional = lista(resposta.possivelHistoricoProfissional);
  const outros = Object.entries(resposta).filter(([k]) => !BLOCOS_TRATADOS.has(k));
  const cabecalho = obj(data.consulta.resultado?.cabecalho);
  const regular = String(cad.situacaoCadastral || '').toUpperCase() === 'REGULAR';

  return (
    <div className="space-y-3">
      <Secao icone={<Fingerprint className="h-3 w-3 text-muted-foreground" />} titulo="Dados do CPF · Assertiva">
        <div className="flex flex-wrap items-center gap-1.5 pb-1.5">
          <Selo bom={regular}>Receita: {txt(cad.situacaoCadastral) || 'sem situação'}</Selo>
          <Selo bom={cad.obitoProvavel !== true}>{cad.obitoProvavel === true ? 'Óbito provável' : 'Sem óbito'}</Selo>
          {cad.ppe === true && <Selo bom={false}>PPE</Selo>}
          <span className="text-[10px] text-muted-foreground">consultado em {new Date(data.consulta.created_at).toLocaleDateString('pt-BR')} · não é consultado de novo</span>
        </div>
        <div className="grid gap-x-6 md:grid-cols-2">
          {Object.entries(cad).map(([k, v]) => (
            <Par key={k} label={ROTULOS[k] || k} valor={k === 'cpf' && txt(v) ? formatarCPF(String(v)) : valorLegivel(v)} />
          ))}
        </div>
      </Secao>

      <Secao icone={<Phone className="h-3 w-3 text-muted-foreground" />} titulo="Telefones" qtd={fones.length}>
        {fones.length === 0 ? <p className="py-1 text-xs text-muted-foreground">Nenhum telefone.</p> : fones.map((t, i) => {
          const apps = obj(t.aplicativos);
          return (
            <Par key={i} label={`${txt(t.numero) || '—'} · ${t.tipo}`} valor={
              <span className="font-normal">
                {[txt(t.operadora), apps.whatsApp === true || apps.whatsAppBusiness === true ? 'WhatsApp' : null,
                  t.naoPerturbe === true ? 'NÃO PERTURBE' : null, txt(t.relacao), txt(t.ultimoContato),
                  t.hotphone === true ? 'hotphone' : null, t.plus === true ? 'plus' : null].filter(Boolean).join(' · ')}
              </span>
            } />
          );
        })}
      </Secao>

      <Secao icone={<Mail className="h-3 w-3 text-muted-foreground" />} titulo="E-mails" qtd={emails.length}>
        {emails.length === 0 ? <p className="py-1 text-xs text-muted-foreground">Nenhum e-mail.</p>
          : emails.map((e, i) => <Par key={i} label={`E-mail ${i + 1}`} valor={txt(e.email) || valorLegivel(e)} />)}
      </Secao>

      <Secao icone={<MapPin className="h-3 w-3 text-muted-foreground" />} titulo="Endereços" qtd={enderecos.length}>
        {enderecos.length === 0 ? <p className="py-1 text-xs text-muted-foreground">Nenhum endereço.</p> : enderecos.map((e, i) => (
          <Par key={i} label={[txt(e.cidade), txt(e.uf)].filter(Boolean).join('/') || `Endereço ${i + 1}`} valor={
            <span className="font-normal">
              {[[txt(e.tipoLogradouro), txt(e.logradouro)].filter(Boolean).join(' '), txt(e.numero), txt(e.complemento), txt(e.bairro),
                txt(e.cep) ? `CEP ${e.cep}` : null, txt(e.precisaoCep), e.latitude ? `${e.latitude}, ${e.longitude}` : null].filter(Boolean).join(' · ')}
            </span>
          } />
        ))}
      </Secao>

      <Secao icone={<Building2 className="h-3 w-3 text-muted-foreground" />} titulo="Empresas em que é sócio" qtd={empresas.length}>
        {empresas.length === 0 ? <p className="py-1 text-xs text-muted-foreground">Nenhuma participação em empresa.</p> : empresas.map((e, i) => (
          <Par key={i} label={txt(e.razaoSocial) || `Empresa ${i + 1}`} valor={
            <span className="font-normal">{[txt(e.cnpj), txt(e.cargo), txt(e.dataEntrada) ? `desde ${e.dataEntrada}` : null].filter(Boolean).join(' · ')}</span>
          } />
        ))}
      </Secao>

      <Secao icone={<Briefcase className="h-3 w-3 text-muted-foreground" />} titulo="Histórico profissional e renda" qtd={profissional.length}>
        {profissional.length === 0 ? <p className="py-1 text-xs text-muted-foreground">Nenhum registro.</p> : profissional.map((p, i) => (
          <Par key={i} label={txt(p.razaoSocial) || `Registro ${i + 1}`} valor={
            <span className="font-normal">{[txt(p.cboDescricao), txt(p.setor), txt(p.cnpj), txt(p.dataRegistro), txt(p.faixaSalarial),
              txt(p.rendaEstimada) ? `renda estimada R$ ${p.rendaEstimada}` : null].filter(Boolean).join(' · ')}</span>
          } />
        ))}
      </Secao>

      <Secao icone={<Fingerprint className="h-3 w-3 text-muted-foreground" />} titulo="Demais informações da Assertiva">
        {outros.map(([k, v]) => <Par key={k} label={k} valor={valorLegivel(v) ?? (Array.isArray(v) && v.length === 0 ? 'nenhum' : null)} />)}
        {lista(obj(resposta.feedbackTelefones).moveis).length + lista(obj(resposta.feedbackTelefones).fixos).length > 0 && (
          <Par label="feedbackTelefones" valor={valorLegivel(resposta.feedbackTelefones)} />
        )}
        {Object.entries(cabecalho).map(([k, v]) => <Par key={`cab-${k}`} label={`consulta · ${k}`} valor={valorLegivel(v)} />)}
      </Secao>
    </div>
  );
}
